/**
 * Pure, deterministic arithmetic for the AI Automation ROI Calculator
 * ({@link ../components/tools/ai-automation-roi-calculator.tsx}).
 *
 * SINGLE SOURCE OF TRUTH for the ROI maths so the widget and the unit tests
 * ({@link ./automation-roi.test.ts}) compute the exact same numbers. No I/O, no
 * Date.now/Math.random, no rounding surprises: every function is a pure mapping
 * from inputs to a result, which keeps it SSR-safe and trivially testable.
 *
 * All money is in a single currency (the widget formats it with Intl in the
 * active locale); all time is in hours. No em dashes anywhere.
 */

/** The raw inputs a user supplies in the calculator form. */
export type RoiInput = {
  /** Hours one person spends on the task per week. */
  hoursPerWeek: number
  /** How many people do the task. */
  people: number
  /** Fully loaded labour cost per hour, in the active currency. */
  hourlyCost: number
  /** Share of the work the automation can take over, 0..100 (percent). */
  automatablePercent: number
  /** One-time build/setup effort in hours (valued at the hourly cost). */
  setupHours: number
  /** One-time setup cost on top of the setup hours (tools bought, etc.). */
  setupCost: number
  /** Recurring monthly tool/subscription cost to run the automation. */
  monthlyToolCost: number
}

/** The computed result panel, all in the active currency unless noted. */
export type RoiResult = {
  /** Hours saved across all people per month (4.33 weeks/month). */
  hoursSavedPerMonth: number
  /** Hours saved across all people per year. */
  hoursSavedPerYear: number
  /** Gross labour value of the time saved per month. */
  grossMonthlySaving: number
  /** Net monthly saving after the recurring tool cost. */
  netMonthlySaving: number
  /** Net saving over the first 12 months, after the one-time setup. */
  firstYearNetSaving: number
  /** Net annual saving in a steady-state year (no setup cost). */
  annualNetSaving: number
  /** Total one-time investment: setup hours valued at cost + setup cost. */
  totalSetupInvestment: number
  /**
   * Months to recoup the one-time setup from the net monthly saving, or null
   * when it never pays back (net monthly saving <= 0) or there is nothing to
   * recoup. Use the null case to show a "does not pay back" state.
   */
  paybackMonths: number | null
  /**
   * First-year ROI as a fraction (0.5 == 50%): firstYearNetSaving divided by
   * the total first-year investment. Null when there is no investment.
   */
  roiFraction: number | null
}

/**
 * Average number of weeks in a month (52 / 12). Used to convert a weekly time
 * figure into a monthly one. Kept as a named constant so the widget label and
 * the maths can never drift apart.
 */
export const WEEKS_PER_MONTH = 52 / 12

/** Clamp a value into [min, max]. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** Coerce a possibly-NaN/negative number to a finite, non-negative number. */
function nonNeg(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0
  return value
}

/**
 * Compute the full ROI result from the user inputs. Pure and deterministic.
 *
 * Formulae:
 *   automatable        = clamp(automatablePercent, 0, 100) / 100
 *   hoursSavedPerWeek  = hoursPerWeek * people * automatable
 *   hoursSavedPerMonth = hoursSavedPerWeek * WEEKS_PER_MONTH
 *   grossMonthlySaving = hoursSavedPerMonth * hourlyCost
 *   netMonthlySaving   = grossMonthlySaving - monthlyToolCost
 *   totalSetup         = setupHours * hourlyCost + setupCost
 *   firstYearNet       = netMonthlySaving * 12 - totalSetup
 *   annualNet          = netMonthlySaving * 12
 *   paybackMonths      = netMonthlySaving > 0 ? totalSetup / netMonthlySaving : null
 *   roiFraction        = (totalSetup + monthlyToolCost*12) > 0
 *                          ? firstYearNet / (totalSetup + monthlyToolCost*12)
 *                          : null
 */
export function computeRoi(input: RoiInput): RoiResult {
  const hoursPerWeek = nonNeg(input.hoursPerWeek)
  const people = nonNeg(input.people)
  const hourlyCost = nonNeg(input.hourlyCost)
  const automatable = clamp(nonNeg(input.automatablePercent), 0, 100) / 100
  const setupHours = nonNeg(input.setupHours)
  const setupCost = nonNeg(input.setupCost)
  const monthlyToolCost = nonNeg(input.monthlyToolCost)

  const hoursSavedPerWeek = hoursPerWeek * people * automatable
  const hoursSavedPerMonth = hoursSavedPerWeek * WEEKS_PER_MONTH
  const hoursSavedPerYear = hoursSavedPerWeek * 52

  const grossMonthlySaving = hoursSavedPerMonth * hourlyCost
  const netMonthlySaving = grossMonthlySaving - monthlyToolCost

  const totalSetupInvestment = setupHours * hourlyCost + setupCost

  const annualNetSaving = netMonthlySaving * 12
  const firstYearNetSaving = annualNetSaving - totalSetupInvestment

  // Payback is only meaningful when the automation nets a positive monthly
  // saving; otherwise it never recoups the setup.
  const paybackMonths =
    netMonthlySaving > 0 ? totalSetupInvestment / netMonthlySaving : null

  // First-year ROI compares the first-year net benefit to everything you put in
  // over that year (the one-time setup plus twelve months of tool cost).
  const firstYearInvestment = totalSetupInvestment + monthlyToolCost * 12
  const roiFraction =
    firstYearInvestment > 0 ? firstYearNetSaving / firstYearInvestment : null

  return {
    hoursSavedPerMonth,
    hoursSavedPerYear,
    grossMonthlySaving,
    netMonthlySaving,
    firstYearNetSaving,
    annualNetSaving,
    totalSetupInvestment,
    paybackMonths,
    roiFraction,
  }
}
